import React, { useState, useCallback, useEffect, useRef } from 'react';
import { reviewCode, generateCode } from '@snipit/core/services/geminiService';
import { PROGRAMMING_LANGUAGES } from './GeminiReview/constants';
import CodeInput from './GeminiReview/CodeInput';
import LanguageSelector from './GeminiReview/LanguageSelector';
import ReviewOutput from './GeminiReview/ReviewOutput';
import Loader from './GeminiReview/Loader';
import ErrorMessage from './GeminiReview/ErrorMessage';
import { Sparkles, ArrowLeft, Code, Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@snipit/core/services/analytics';

const GeminiReviewView = ({ initialCode = '', initialLanguage = PROGRAMMING_LANGUAGES[0], onBack }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(initialCode);
  const [language, setLanguage] = useState(
    PROGRAMMING_LANGUAGES.includes(initialLanguage) ? initialLanguage : PROGRAMMING_LANGUAGES[0]
  );
  const [feedback, setFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasAutoRun = useRef(false);

  const handleAction = useCallback(async () => {
    if (!code.trim()) {
      setError('Please enter some code to review.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setFeedback('');
    trackEvent('ai_review_requested', { language });

    try {
      const result = await reviewCode(code, language);
      setFeedback(result);
    } catch (err) {
      setError(`Failed to review from Gemini API. Please check your API key and try again.`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [code, language]);

  useEffect(() => {
    if (initialCode && !hasAutoRun.current) {
      hasAutoRun.current = true;
      handleAction();
    }
  }, [initialCode, handleAction]);

  return (
    <div className="flex flex-col h-full bg-background text-slate-200">
      <div className="p-6 border-b border-gray-800 flex flex-col space-y-4">
        <div className="flex items-center space-x-3">
          {onBack && (
            <button onClick={onBack} className="p-2 mr-2 rounded-full hover:bg-gray-800 text-gray-400 hover:text-white transition-colors" title="Go Back">
              <ArrowLeft size={20} />
            </button>
          )}
          <Sparkles className="w-8 h-8 text-cyan-400" />
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-linear-to-r from-blue-400 to-indigo-500">{t('Gemini AI Tools (Pro)')}</h1>
        </div>
        
        {/* Tabs Removed */}
      </div>
      
      <div className="grow p-6 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
          {/* Input Section */}
          <div className="flex flex-col space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t('Your Code')}</h2>
              <LanguageSelector 
                selectedLanguage={language}
                onLanguageChange={setLanguage}
                languages={PROGRAMMING_LANGUAGES}
              />
            </div>
            <CodeInput code={code} onCodeChange={setCode} />
            <button
              onClick={handleAction}
              disabled={isLoading}
              className={`flex items-center justify-center w-full disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 shadow-lg bg-cyan-600 hover:bg-cyan-500 focus:ring-cyan-400`}
            >
              {isLoading ? (
                <>
                  <Loader />
                  <span>{t('Reviewing...')}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  <span>{t('Review Code')}</span>
                </>
              )}
            </button>
            {error && !isLoading && <ErrorMessage message={error} />}
          </div>
          
          {/* Output Section */}
          <div className="flex flex-col space-y-4 h-125 lg:h-auto">
             <h2 className="text-lg font-semibold">{t("Gemini's Output")}</h2>
            <ReviewOutput feedback={feedback} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeminiReviewView;
